import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Popover from "@mui/material/Popover";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";

import MicIcon from "@mui/icons-material/Mic";
import VideocamIcon from "@mui/icons-material/Videocam";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";

import { AuthContext } from "../../context/AuthContext";
import { LibrettoRecordingMode } from "../../utils/utils";
import { SoloSingleFileAudioRecording, generateSoloTrackId } from "../../localRecording/SoloSingleFileAudioRecording";
import { SoloSingleFileVideoRecording } from "../../localRecording/SoloSingleFileVideoRecording";
import { SoloSingleFileScreenRecording } from "../../localRecording/SoloSingleFileScreenRecording";
import { trackSessionEvent } from "../../analytics/sessionEvents";
import { useAssetStore } from "../../storage/AssetStore";
import { ProjectAssetsStore } from "../../state/ProjectAssetsStore";
import {MicNoneOutlined, ScreenShareOutlined, VideocamOutlined, PauseOutlined, PlayArrowOutlined, SubtitlesOutlined} from "@mui/icons-material";
import TeleprompterWindow from "../../components/Teleprompter/TeleprompterWindow";
import {
    getPreferredAudioInputDeviceId,
    getPreferredVideoInputDeviceId,
    setPreferredAudioInputDeviceId,
    setPreferredVideoInputDeviceId,
} from "../../utils/mediaDevicePreferences";

/**
 * LocalStudio - Solo recording component that works offline.
 * Records directly to a single OPUS (audio) or MP4 (video) file.
 * After recording stops, file is immediately ready and uploaded via UploadAsset.
 * 
 * Recording mode is determined by camera selection:
 * - Camera selected → Video mode (records video + audio)
 * - No camera selected → Audio mode (records audio only)
 * 
 * Simplified layout: Video/Audio preview on top, 52px control bar at bottom.
 */

const CONTROL_BAR_HEIGHT = 56;

// Audio visualizer smoothing
const SMOOTHING_FACTOR = 0.04; // Lower = smoother/slower decay
const RISE_FACTOR = 0.12; // How fast bars rise (lower = slower)
const VISUALIZER_FPS = 24; // Target frame rate for bar updates
const AMPLITUDE_BOOST = 2.0; // Multiplier to exaggerate small sounds
const AMPLITUDE_POWER = 0.5; // Power curve to boost quiet sounds (lower = more boost)

// Resolution options for camera
const RESOLUTION_OPTIONS = [
    { label: '480p', width: 854, height: 480 },
    { label: '720p', width: 1280, height: 720 },
    { label: '1080p', width: 1920, height: 1080 },
    { label: '1440p', width: 2560, height: 1440 },
    { label: '4K', width: 3840, height: 2160 },
];

const pickAvailableDeviceId = (devices, ...candidateIds) => {
    const normalizedIds = candidateIds
        .map((id) => (typeof id === 'string' ? id.trim() : ''))
        .filter(Boolean);

    for (const candidateId of normalizedIds) {
        if (devices.some((device) => device.deviceId === candidateId)) {
            return candidateId;
        }
    }

    return '';
};

const LocalStudio = ({
    projectId,
    initialTitle,
    recordingMode, // Optional hint: LibrettoRecordingMode.Video will auto-select camera
    isScreenRecording = false,
    onRecordingComplete,
    onLeave, // Optional callback, otherwise navigates to dashboard
}) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";
    const authContext = useContext(AuthContext);

    // Title for the recording - always use display name + type format with counter
    // Counts existing recordings in project to generate sequential names
    const generateRecordingTitle = () => {
        const displayName = authContext.getUserDisplayName?.() || '';
        const firstName = displayName.split(' ')[0];
        const type = isScreenRecording ? 'Screen' : (recordingMode === LibrettoRecordingMode.Video ? 'Video' : 'Audio');
        const baseTitle = firstName ? `${firstName}'s ${type}` : `My ${type}`;
        
        // Count existing recordings with the same base title pattern in this project
        const tracks = ProjectAssetsStore.projectTracks || [];
        const existingTitles = tracks.map(t => t.title || t.name || '');
        
        // Check if base title exists
        if (!existingTitles.includes(baseTitle)) {
            return baseTitle;
        }
        
        // Find the next available number
        let counter = 1;
        while (existingTitles.includes(`${baseTitle} - ${counter}`)) {
            counter++;
        }
        return `${baseTitle} - ${counter}`;
    };
    const [titleValue] = useState(generateRecordingTitle);

    // Device state
    const [audioDevices, setAudioDevices] = useState([]);
    const [videoDevices, setVideoDevices] = useState([]);
    const [selectedAudioDevice, setSelectedAudioDevice] = useState(() => getPreferredAudioInputDeviceId());
    const [selectedVideoDevice, setSelectedVideoDevice] = useState(() => (
        recordingMode === LibrettoRecordingMode.Video || isScreenRecording
            ? getPreferredVideoInputDeviceId()
            : ''
    )); // Empty = audio only mode

    // Settings state
    const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
    const [noiseSuppressionEnabled, setNoiseSuppressionEnabled] = useState(true); // On by default
    const [autoGainControlEnabled, setAutoGainControlEnabled] = useState(true);
    const [selectedResolution, setSelectedResolution] = useState('1080p');
    const [teleprompterOpen, setTeleprompterOpen] = useState(false);
    const settingsOpen = Boolean(settingsAnchorEl);

    // Preview state
    const [previewStream, setPreviewStream] = useState(null);
    const previewStreamRef = useRef(null);
    const videoPreviewRef = useRef(null);

    // Screen preview state
    const [screenStream, setScreenStream] = useState(null);
    const screenStreamRef = useRef(null);
    const screenVideoPreviewRef = useRef(null);
    const screenShareButtonRef = useRef(null);

    // Audio visualizer
    const canvasRef = useRef(null);
    const analyserRef = useRef(null);
    const audioContextRef = useRef(null);
    const animationFrameRef = useRef(null);
    const smoothedValuesRef = useRef(null); // For temporal smoothing
    const lastDrawTimeRef = useRef(0); // For frame rate limiting

    // Recording state
    const [countdown, setCountdown] = useState(null);
    const [recordingInProgress, setRecordingInProgress] = useState(false);
    const [recordingStarting, setRecordingStarting] = useState(false);
    const [isStopping, setIsStopping] = useState(false);
    const [recordingPaused, setRecordingPaused] = useState(false);
    const [recordTime, setRecordTime] = useState(0);
    const recordTimerRef = useRef(null);
    const pauseActionInFlightRef = useRef(false);

    // Video mode audio indicator
    const videoAudioAnalyserRef = useRef(null);
    const videoAudioLevelRef = useRef(0);
    const audioIndicatorRef = useRef(null);
    const audioIndicatorAnimationRef = useRef(null);

    const audioRecorderRef = useRef(null);
    const videoRecorderRef = useRef(null);
    const screenRecorderRef = useRef(null);
    const projectIdRef = useRef(projectId);
    const autoScreenShareRequestedRef = useRef(false);

    const { CreateProject, UploadAsset } = useAssetStore();

    // Video mode is determined by whether a camera is selected
    const isVideoMode = Boolean(selectedVideoDevice);
    const hasScreenStream = Boolean(screenStream);
    const isScreenLayoutMode = Boolean(hasScreenStream);
    const hasAnyInputSelected = Boolean(selectedAudioDevice) || Boolean(selectedVideoDevice) || Boolean(screenStream);
    const canStartRecording = hasAnyInputSelected;

    useEffect(() => {
        if (selectedAudioDevice) {
            setPreferredAudioInputDeviceId(selectedAudioDevice);
        }
    }, [selectedAudioDevice]);

    useEffect(() => {
        if (selectedVideoDevice) {
            setPreferredVideoInputDeviceId(selectedVideoDevice);
        }
    }, [selectedVideoDevice]);

    const getScreenAspectRatio = () => {
        const videoTrack = screenStream?.getVideoTracks?.()?.[0];
        const settings = videoTrack?.getSettings?.();
        const width = settings?.width;
        const height = settings?.height;
        if (Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0) {
            return `${Math.round(width)} / ${Math.round(height)}`;
        }
        return '16 / 9';
    };

    const getScreenShareLabel = () => {
        if (!screenStream) return 'No screen shared';
        const videoTrack = screenStream.getVideoTracks()[0];
        const displaySurface = videoTrack?.getSettings?.()?.displaySurface;

        if (displaySurface === 'monitor') return 'Sharing full screen';
        if (displaySurface === 'window') return 'Sharing a window';
        if (displaySurface === 'browser') return 'Sharing a tab';
        return 'Sharing screen';
    };

    // Set page title
    useEffect(() => {
        document.title = titleValue || "Recording Studio";
    }, [titleValue]);

    // Enumerate devices on mount and request mic/cam permission so labels + defaults are available.
    useEffect(() => {
        let cancelled = false;
        async function enumerateDevices() {
            const applyDetectedDevices = (audioInputs, videoInputs) => {
                setAudioDevices(audioInputs);
                setVideoDevices(videoInputs);

                const preferredAudioDeviceId = getPreferredAudioInputDeviceId();
                const resolvedAudioDeviceId = pickAvailableDeviceId(
                    audioInputs,
                    selectedAudioDevice,
                    preferredAudioDeviceId,
                    audioInputs[0]?.deviceId || '',
                );
                setSelectedAudioDevice(resolvedAudioDeviceId);

                const shouldAutoSelectVideoDevice =
                    recordingMode === LibrettoRecordingMode.Video || isScreenRecording;

                if (!shouldAutoSelectVideoDevice) {
                    return;
                }

                const preferredVideoDeviceId = getPreferredVideoInputDeviceId();
                const resolvedVideoDeviceId = pickAvailableDeviceId(
                    videoInputs,
                    selectedVideoDevice,
                    preferredVideoDeviceId,
                    videoInputs[0]?.deviceId || '',
                );
                setSelectedVideoDevice(resolvedVideoDeviceId);
            };

            try {
                // Request both audio and video permissions to enumerate all devices with labels
                // Stop tracks immediately; the preview stream will be created separately below.
                let tmpStream = null;
                try {
                    tmpStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
                } catch (e) {
                    try {
                        tmpStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
                    } catch { }
                } finally {
                    try { tmpStream?.getTracks?.()?.forEach(t => t.stop()); } catch { }
                }

                const devices = await navigator.mediaDevices.enumerateDevices();
                if (cancelled) return;
                const audioInputs = devices.filter(d => d.kind === 'audioinput');
                const videoInputs = devices.filter(d => d.kind === 'videoinput');

                applyDetectedDevices(audioInputs, videoInputs);
            } catch (err) {
                console.error('[LocalStudio] Failed to enumerate devices:', err);
                // Try audio-only if video permission denied
                try {
                    let tmpStream = null;
                    try {
                        tmpStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                    } catch { }
                    try { tmpStream?.getTracks?.()?.forEach(t => t.stop()); } catch { }
                    const devices = await navigator.mediaDevices.enumerateDevices();
                    if (cancelled) return;
                    const audioInputs = devices.filter(d => d.kind === 'audioinput');
                    const videoInputs = devices.filter(d => d.kind === 'videoinput');

                    applyDetectedDevices(audioInputs, videoInputs);
                } catch (audioErr) {
                    console.error('[LocalStudio] Failed to get audio permission:', audioErr);
                }
            }
        }

        enumerateDevices();
        return () => {
            cancelled = true;
        };
    }, [recordingMode, isScreenRecording]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (previewStreamRef.current) {
                previewStreamRef.current.getTracks().forEach(track => track.stop());
                previewStreamRef.current = null;
            }
            if (screenStreamRef.current) {
                screenStreamRef.current.getTracks().forEach(track => track.stop());
                screenStreamRef.current = null;
            }
            if (audioRecorderRef.current && audioRecorderRef.current.isRecording()) {
                audioRecorderRef.current.stop().catch(() => {});
            }
            if (videoRecorderRef.current && videoRecorderRef.current.isRecording()) {
                videoRecorderRef.current.stop().catch(() => {});
            }
            if (screenRecorderRef.current && screenRecorderRef.current.isRecording()) {
                screenRecorderRef.current.stop().catch(() => {});
            }
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            if (audioContextRef.current) {
                audioContextRef.current.close().catch(() => {});
            }
        };
    }, []);

    // Update preview stream when device selection or settings change
    useEffect(() => {
        async function updatePreview() {
            if (previewStreamRef.current) {
                previewStreamRef.current.getTracks().forEach(track => track.stop());
                previewStreamRef.current = null;
            }

            if (!selectedAudioDevice && !selectedVideoDevice) {
                setPreviewStream(null);
                return;
            }

            try {
                // Get resolution settings
                const resolution = RESOLUTION_OPTIONS.find(r => r.label === selectedResolution) || RESOLUTION_OPTIONS[2];

                const constraints = {
                    audio: selectedAudioDevice
                        ? {
                            deviceId: { exact: selectedAudioDevice },
                            autoGainControl: autoGainControlEnabled,
                            echoCancellation: noiseSuppressionEnabled,
                            noiseSuppression: noiseSuppressionEnabled,
                        }
                        : false,
                    video: isVideoMode && selectedVideoDevice
                        ? { 
                            deviceId: { exact: selectedVideoDevice },
                            width: { ideal: resolution.width },
                            height: { ideal: resolution.height },
                        }
                        : isVideoMode 
                            ? {
                                width: { ideal: resolution.width },
                                height: { ideal: resolution.height },
                            }
                            : false,
                };

                const stream = await navigator.mediaDevices.getUserMedia(constraints);
                previewStreamRef.current = stream;
                setPreviewStream(stream);
            } catch (err) {
                console.error('[LocalStudio] Failed to get preview stream:', err);
            }
        }

        updatePreview();

        return () => {
            if (previewStreamRef.current) {
                previewStreamRef.current.getTracks().forEach(track => track.stop());
                previewStreamRef.current = null;
            }
        };
    }, [selectedAudioDevice, selectedVideoDevice, isVideoMode, autoGainControlEnabled, noiseSuppressionEnabled, selectedResolution]);

    // In screen recording mode we avoid prompting for mic/cam permissions on mount, which means
    // `enumerateDevices()` may return empty labels. Once the user grants permission via preview,
    // re-enumerate so the dropdowns can show real device names.
    useEffect(() => {
        if (!isScreenRecording) return;
        if (!previewStream) return;

        let cancelled = false;
        (async () => {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                if (cancelled) return;
                setAudioDevices(devices.filter(d => d.kind === 'audioinput'));
                setVideoDevices(devices.filter(d => d.kind === 'videoinput'));
            } catch (err) {
                console.error('[LocalStudio] Failed to re-enumerate devices:', err);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isScreenRecording, previewStream]);

    const tryPlayVideoElement = (el) => {
        if (!el) return;
        try {
            const p = el.play?.();
            if (p && typeof p.catch === 'function') {
                p.catch(() => {});
            }
        } catch { }
    };

    // Attach preview stream to whichever video element is currently mounted.
    // `videoPreviewRef` is reused across layouts (main camera preview vs. PiP overlay),
    // so re-attach whenever screen layout mode toggles.
    useEffect(() => {
        const el = videoPreviewRef.current;
        if (!el) return;
        if (!previewStream) {
            try { el.srcObject = null; } catch { }
            return;
        }

        if (el.srcObject !== previewStream) {
            el.srcObject = previewStream;
        }

        const onLoaded = () => tryPlayVideoElement(el);
        el.addEventListener?.('loadedmetadata', onLoaded);
        tryPlayVideoElement(el);
        return () => {
            el.removeEventListener?.('loadedmetadata', onLoaded);
        };
    }, [previewStream, isScreenLayoutMode]);

    // Attach screen stream to screen video element and ensure playback starts.
    useEffect(() => {
        const el = screenVideoPreviewRef.current;
        if (!el) return;
        if (!screenStream) {
            try { el.srcObject = null; } catch { }
            return;
        }

        if (el.srcObject !== screenStream) {
            el.srcObject = screenStream;
        }

        const onLoaded = () => tryPlayVideoElement(el);
        el.addEventListener?.('loadedmetadata', onLoaded);
        tryPlayVideoElement(el);
        return () => {
            el.removeEventListener?.('loadedmetadata', onLoaded);
        };
    }, [screenStream, hasScreenStream, isScreenLayoutMode]);

    // When returning to the tab after the picker/focus change, nudge video playback.
    useEffect(() => {
        const onVis = () => {
            if (document.visibilityState !== 'visible') return;
            tryPlayVideoElement(videoPreviewRef.current);
            tryPlayVideoElement(screenVideoPreviewRef.current);
        };
        document.addEventListener('visibilitychange', onVis);
        return () => document.removeEventListener('visibilitychange', onVis);
    }, []);

    // If screen share is ended via browser UI, reflect it in our UI.
    useEffect(() => {
        if (!screenStream) return;
        const [videoTrack] = screenStream.getVideoTracks();
        if (!videoTrack) return;

        const handleEnded = () => {
            if (recordingInProgress && !isStopping) {
                handleStopRecording();
                return;
            }

            try {
                screenStream.getTracks().forEach(track => track.stop());
            } catch { }
            setScreenStream(null);
            screenStreamRef.current = null;
        };

        videoTrack.addEventListener('ended', handleEnded);
        return () => {
            videoTrack.removeEventListener('ended', handleEnded);
        };
    }, [screenStream, recordingInProgress, isStopping]);

    const handleToggleScreenShare = async () => {
        if (recordingInProgress || countdown !== null || isStopping) return;

        if (screenStreamRef.current) {
            try {
                screenStreamRef.current.getTracks().forEach(track => track.stop());
            } catch { }
            screenStreamRef.current = null;
            setScreenStream(null);
            return;
        }

        try {
            // Attempt to keep focus on this tab after starting capture (Chrome supports CaptureController).
            let controller;
            try {
                // eslint-disable-next-line no-undef
                const Ctor = window.CaptureController;
                if (typeof Ctor === 'function') {
                    controller = new Ctor();
                    if (typeof controller?.setFocusBehavior === 'function') {
                        try {
                            controller.setFocusBehavior('no-focus-change');
                        } catch { }
                    }
                }
            } catch { }

            const constraints = {
                video: {
                    frameRate: { ideal: 30, max: 60 },
                },
                audio: true, // system/tab audio (best-effort; may be unavailable depending on browser + source)
            };
            if (controller) {
                constraints.controller = controller;
            }

            const stream = await navigator.mediaDevices.getDisplayMedia(constraints);
            try {
                window.focus();
                setTimeout(() => {
                    try { window.focus(); } catch { }
                }, 0);
            } catch { }

            const [track] = stream.getVideoTracks();
            if (track) {
                try {
                    track.contentHint = 'detail';
                } catch { }
            }

            screenStreamRef.current = stream;
            setScreenStream(stream);
        } catch (err) {
            console.error('[LocalStudio] Failed to start screen capture:', err);
        }
    };

    // If launched in screen-recording mode, prompt the user to pick a screen/tab immediately.
    // Best-effort: some browsers require a user gesture; in that case the user can click the button.
    useEffect(() => {
        if (!isScreenRecording) return;
        if (autoScreenShareRequestedRef.current) return;
        if (screenStreamRef.current) return;
        if (recordingInProgress || countdown !== null || isStopping) return;

        autoScreenShareRequestedRef.current = true;
        // Defer until after the UI has rendered (so the Screen Share control is visible).
        let raf1 = 0;
        let raf2 = 0;
        raf1 = window.requestAnimationFrame(() => {
            raf2 = window.requestAnimationFrame(() => {
                if (!screenShareButtonRef.current) return;
                void handleToggleScreenShare();
            });
        });
        return () => {
            if (raf1) window.cancelAnimationFrame(raf1);
            if (raf2) window.cancelAnimationFrame(raf2);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isScreenRecording]);

    // Audio visualizer for audio-only mode - with temporal smoothing
    useEffect(() => {
        if (isVideoMode || !previewStream || !canvasRef.current) return;

        const audioTrack = previewStream.getAudioTracks()[0];
        if (!audioTrack) return;

        const audioContext = new AudioContext();
        audioContextRef.current = audioContext;
        const analyser = audioContext.createAnalyser();
        analyserRef.current = analyser;
        analyser.fftSize = 256;

        const source = audioContext.createMediaStreamSource(previewStream);
        source.connect(analyser);

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        
        // Initialize smoothed values
        const barCount = 20; // Slightly fewer bars for breathing room
        if (!smoothedValuesRef.current || smoothedValuesRef.current.length !== barCount) {
            smoothedValuesRef.current = new Array(barCount).fill(0);
        }
        
        const frameInterval = 1000 / VISUALIZER_FPS;

        const draw = (timestamp) => {
            if (!ctx || !analyser) return;

            animationFrameRef.current = requestAnimationFrame(draw);
            
            // Frame rate limiting
            const elapsed = timestamp - lastDrawTimeRef.current;
            if (elapsed < frameInterval) return;
            lastDrawTimeRef.current = timestamp - (elapsed % frameInterval);

            analyser.getByteTimeDomainData(dataArray);

            const width = canvas.width;
            const height = canvas.height;

            // Clear canvas with softer background
            ctx.fillStyle = isDarkMode ? '#232833' : '#F3F4F5';
            ctx.fillRect(0, 0, width, height);

            // Draw center dashed line - softer, wider spacing
            ctx.strokeStyle = isDarkMode ? '#8C97A6' : '#9CA3AF';
            ctx.lineWidth = 1;
            ctx.setLineDash([16, 16]);
            ctx.beginPath();
            ctx.moveTo(0, height / 2);
            ctx.lineTo(width, height / 2);
            ctx.stroke();
            ctx.setLineDash([]);

            // Draw waveform bars - centered, with smoothing
            const barWidth = 14;
            const gap = 10;
            const totalBarsWidth = barCount * (barWidth + gap) - gap;
            const startX = (width - totalBarsWidth) / 2;
            const maxBarHeight = height * 0.55;
            const minBarHeight = 6;
            const cornerRadius = 3;

            ctx.fillStyle = isDarkMode ? '#A7B0BC' : '#2A2A2A';
            
            for (let i = 0; i < barCount; i++) {
                const dataIndex = Math.floor((i / barCount) * bufferLength);
                const value = dataArray[dataIndex];
                const rawLinear = Math.abs(value - 128) / 128;
                
                // Apply power curve to boost small sounds (makes quiet audio more visible)
                // Then multiply to exaggerate the amplitude
                const rawValue = Math.pow(rawLinear, AMPLITUDE_POWER) * AMPLITUDE_BOOST;
                
                // Temporal smoothing: rise slower, fall even slower
                const currentSmoothed = smoothedValuesRef.current[i];
                let newSmoothed;
                if (rawValue > currentSmoothed) {
                    // Rising: respond moderately (slower than before)
                    newSmoothed = currentSmoothed + (rawValue - currentSmoothed) * RISE_FACTOR;
                } else {
                    // Falling: decay very slowly for sustained motion
                    newSmoothed = currentSmoothed + (rawValue - currentSmoothed) * SMOOTHING_FACTOR;
                }
                smoothedValuesRef.current[i] = newSmoothed;
                
                // Clamp to max 1 to prevent bars going outside bounds
                const clampedValue = Math.min(1, newSmoothed);
                const barHeight = Math.max(minBarHeight, clampedValue * maxBarHeight * 2);
                const x = startX + i * (barWidth + gap);
                const y = (height - barHeight) / 2;

                // Draw rounded rectangle
                ctx.beginPath();
                ctx.roundRect(x, y, barWidth, barHeight, cornerRadius);
                ctx.fill();
            }
        };

        animationFrameRef.current = requestAnimationFrame(draw);

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            source.disconnect();
            analyser.disconnect();
        };
    }, [previewStream, isVideoMode, isDarkMode]);

    // Countdown logic
    useEffect(() => {
        if (countdown === null || countdown <= 0) return;

        const timer = setTimeout(() => {
            setCountdown(prev => {
                if (prev !== null && prev <= 1) {
                    return null;
                }
                return prev !== null ? prev - 1 : null;
            });
        }, 1000);

        return () => clearTimeout(timer);
    }, [countdown]);

    // Recording timer
    useEffect(() => {
        if (recordingInProgress && !recordingPaused) {
            recordTimerRef.current = setInterval(() => {
                setRecordTime(prev => prev + 1);
            }, 1000);
        } else {
            if (recordTimerRef.current) {
                clearInterval(recordTimerRef.current);
                recordTimerRef.current = null;
            }
            if (!recordingInProgress) {
                setRecordTime(0);
                setRecordingPaused(false);
            }
        }

        return () => {
            if (recordTimerRef.current) {
                clearInterval(recordTimerRef.current);
            }
        };
    }, [recordingInProgress, recordingPaused]);

    // Format recording time as MM:SS
    const formatRecordTime = () => {
        const minutes = Math.floor(recordTime / 60).toString().padStart(2, '0');
        const seconds = (recordTime % 60).toString().padStart(2, '0');
        return `${minutes}:${seconds}`;
    };

    // Video mode audio level indicator
    useEffect(() => {
        if (!isVideoMode || !previewStream) {
            // Cleanup
            if (audioIndicatorAnimationRef.current) {
                cancelAnimationFrame(audioIndicatorAnimationRef.current);
                audioIndicatorAnimationRef.current = null;
            }
            return;
        }

        const audioTrack = previewStream.getAudioTracks()[0];
        if (!audioTrack) return;

        // Create audio context and analyser for video mode
        const audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        videoAudioAnalyserRef.current = analyser;

        const source = audioContext.createMediaStreamSource(previewStream);
        source.connect(analyser);

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const updateLevel = () => {
            if (!videoAudioAnalyserRef.current) return;
            
            audioIndicatorAnimationRef.current = requestAnimationFrame(updateLevel);
            analyser.getByteTimeDomainData(dataArray);

            // Calculate RMS level
            let sum = 0;
            for (let i = 0; i < bufferLength; i++) {
                const value = (dataArray[i] - 128) / 128;
                sum += value * value;
            }
            const rms = Math.sqrt(sum / bufferLength);
            
            // Smooth the level
            const smoothing = 0.3;
            videoAudioLevelRef.current = videoAudioLevelRef.current * (1 - smoothing) + rms * smoothing;

            // Update the indicator element
            if (audioIndicatorRef.current) {
                const level = Math.min(1, videoAudioLevelRef.current * 3); // Amplify for visibility
                audioIndicatorRef.current.style.height = `${10 + level * 90}%`;
            }
        };

        updateLevel();

        return () => {
            if (audioIndicatorAnimationRef.current) {
                cancelAnimationFrame(audioIndicatorAnimationRef.current);
            }
            source.disconnect();
            analyser.disconnect();
            audioContext.close();
            videoAudioAnalyserRef.current = null;
        };
    }, [isVideoMode, previewStream]);

    const handleStartRecording = async () => {
        if (recordingInProgress || countdown !== null) return;
        if (!canStartRecording) return;

        const shouldRecordScreen = Boolean(screenStreamRef.current);
        const shouldRecordVideo = shouldRecordScreen
            ? true
            : isScreenRecording
                ? Boolean(selectedVideoDevice)
                : recordingMode === LibrettoRecordingMode.Video ||
                  (recordingMode !== LibrettoRecordingMode.Audio && isVideoMode);

        if (shouldRecordScreen) {
            trackSessionEvent("solo_screen_recording_started");
        } else {
            trackSessionEvent(shouldRecordVideo ? "solo_video_recording_started" : "solo_audio_recording_started");
        }

        setRecordingStarting(true);
        setCountdown(3); // 3-second countdown
    };

    const handleTogglePauseRecording = async () => {
        if (!recordingInProgress) return;
        if (isStopping) return;
        if (pauseActionInFlightRef.current) return;
        pauseActionInFlightRef.current = true;

        const shouldPause = !recordingPaused;
        try {
            const recorders = [screenRecorderRef.current, videoRecorderRef.current, audioRecorderRef.current].filter(Boolean);
            const tasks = recorders
                .map((recorder) => {
                    const fn = shouldPause ? recorder?.pause : recorder?.resume;
                    if (typeof fn !== 'function') return null;
                    try {
                        return Promise.resolve(fn.call(recorder));
                    } catch (err) {
                        console.warn('[LocalStudio] Failed to toggle pause:', err);
                        return null;
                    }
                })
                .filter(Boolean);

            await Promise.allSettled(tasks);
            setRecordingPaused(shouldPause);
        } finally {
            pauseActionInFlightRef.current = false;
        }
    };

    const actuallyStartRecording = async () => {
        try {
            // Create project if one doesn't exist
            let currentProjectId = projectIdRef.current;
            if (!currentProjectId) {
                currentProjectId = await CreateProject(titleValue, false);
                projectIdRef.current = currentProjectId;
            }

            // Get resolution settings
            const resolution = RESOLUTION_OPTIONS.find(r => r.label === selectedResolution) || RESOLUTION_OPTIONS[2];

            const shouldRecordScreen = Boolean(screenStreamRef.current);

            if (shouldRecordScreen) {
                // 1) Screen recording (screen video + best-effort screen/system audio)
                const screenTrackId = generateSoloTrackId();
                const screenRecorder = new SoloSingleFileScreenRecording({
                    trackId: screenTrackId,
                    screenStream: screenStreamRef.current,
                    title: titleValue,
                    projectId: currentProjectId,
                });
                await screenRecorder.start();
                screenRecorderRef.current = screenRecorder;

                // 2) Secondary recording (webcam + optional mic, or mic-only)
                const shouldRecordSecondaryVideo = Boolean(selectedVideoDevice);
                const shouldRecordSecondaryAudio = Boolean(selectedAudioDevice);

                if (shouldRecordSecondaryVideo) {
                    const secondaryTrackId = generateSoloTrackId();
                    const previewStream = previewStreamRef.current;
                    const inputStream =
                        previewStream && typeof previewStream.getVideoTracks === 'function' && previewStream.getVideoTracks().length > 0
                            ? previewStream
                            : undefined;
                    const recorder = new SoloSingleFileVideoRecording({
                        trackId: secondaryTrackId,
                        audioDeviceId: selectedAudioDevice || undefined,
                        disableAudio: !shouldRecordSecondaryAudio,
                        videoDeviceId: selectedVideoDevice || undefined,
                        inputStream,
                        title: titleValue,
                        projectId: currentProjectId,
                        width: resolution.width,
                        height: resolution.height,
                        autoGainControl: autoGainControlEnabled,
                        noiseSuppression: noiseSuppressionEnabled,
                    });

                    await recorder.start();
                    videoRecorderRef.current = recorder;
                } else if (shouldRecordSecondaryAudio) {
                    const secondaryTrackId = generateSoloTrackId();
                    const recorder = new SoloSingleFileAudioRecording({
                        trackId: secondaryTrackId,
                        deviceId: selectedAudioDevice || undefined,
                        title: titleValue,
                        projectId: currentProjectId,
                        autoGainControl: autoGainControlEnabled,
                        noiseSuppression: noiseSuppressionEnabled,
                    });

                    await recorder.start();
                    audioRecorderRef.current = recorder;
                }
            } else {
                // Existing solo recording: video (camera + optional mic) or audio-only
                const trackId = generateSoloTrackId();

                // Use recordingMode prop if set, otherwise fall back to device-based detection
                // This prevents race condition where device enumeration hasn't completed yet
                const shouldRecordVideo = isScreenRecording
                    ? isVideoMode
                    : recordingMode === LibrettoRecordingMode.Video ||
                      (recordingMode !== LibrettoRecordingMode.Audio && isVideoMode);

                if (shouldRecordVideo) {
                    const recorder = new SoloSingleFileVideoRecording({
                        trackId,
                        audioDeviceId: selectedAudioDevice || undefined,
                        disableAudio: !selectedAudioDevice,
                        videoDeviceId: selectedVideoDevice || undefined,
                        inputStream:
                            previewStreamRef.current &&
                            typeof previewStreamRef.current.getVideoTracks === 'function' &&
                            previewStreamRef.current.getVideoTracks().length > 0
                                ? previewStreamRef.current
                                : undefined,
                        title: titleValue,
                        projectId: currentProjectId,
                        width: resolution.width,
                        height: resolution.height,
                        autoGainControl: autoGainControlEnabled,
                        noiseSuppression: noiseSuppressionEnabled,
                    });

                    await recorder.start();
                    videoRecorderRef.current = recorder;
                } else {
                    const recorder = new SoloSingleFileAudioRecording({
                        trackId,
                        deviceId: selectedAudioDevice || undefined,
                        title: titleValue,
                        projectId: currentProjectId,
                        autoGainControl: autoGainControlEnabled,
                        noiseSuppression: noiseSuppressionEnabled,
                    });

                    await recorder.start();
                    audioRecorderRef.current = recorder;
                }
            }

            setRecordingInProgress(true);
            setRecordingPaused(false);
            setRecordingStarting(false);
            console.log('[LocalStudio] Recording started');

        } catch (err) {
            console.error('[LocalStudio] Failed to start recording:', err);
            setRecordingStarting(false);
            setRecordingInProgress(false);
            setCountdown(null);
            // Stop and clear any partial recorder refs
            try { await screenRecorderRef.current?.stop(); } catch { }
            try { await videoRecorderRef.current?.stop(); } catch { }
            try { await audioRecorderRef.current?.stop(); } catch { }
            screenRecorderRef.current = null;
            audioRecorderRef.current = null;
            videoRecorderRef.current = null;
        }
    };

    // Begin recording once the countdown finishes.
    useEffect(() => {
        if (!recordingStarting) return;
        if (recordingInProgress) return;
        if (countdown !== null) return;
        void actuallyStartRecording();
    }, [recordingStarting, recordingInProgress, countdown]);

    const handleStopRecording = async () => {
        if (!recordingInProgress || isStopping) return;

        setIsStopping(true);

        try {
            const displayName = authContext.getUserDisplayName?.() || '';
            const firstName = displayName.split(' ')[0] || '';
            const baseName = firstName || displayName;
            const secondaryTitle = baseName || 'My';
            const screenTitle = baseName ? `${baseName}'s screen` : 'My screen';
            const isScreenSession = Boolean(screenRecorderRef.current);

            const stopTasks = [];
            if (screenRecorderRef.current) {
                stopTasks.push({
                    kind: 'screen',
                    filetype: 'video',
                    assetTitle: screenTitle,
                    isScreenRecording: true,
                    stop: () => screenRecorderRef.current.stop()
                });
            }
            if (videoRecorderRef.current) {
                stopTasks.push({
                    kind: 'secondary',
                    filetype: 'video',
                    assetTitle: isScreenSession ? secondaryTitle : `${titleValue} (Webcam)`,
                    isScreenRecording: false,
                    stop: () => videoRecorderRef.current.stop()
                });
            }
            if (audioRecorderRef.current) {
                stopTasks.push({
                    kind: 'secondary',
                    filetype: 'audio',
                    assetTitle: isScreenSession ? secondaryTitle : `${titleValue} (Mic)`,
                    isScreenRecording: false,
                    stop: () => audioRecorderRef.current.stop()
                });
            }
            if (stopTasks.length === 0) {
                console.warn('[LocalStudio] No active recorder to stop');
                setRecordingInProgress(false);
                setIsStopping(false);
                return;
            }

            const stopped = await Promise.allSettled(stopTasks.map(t => t.stop()));

            // Clear refs regardless of stop outcome
            screenRecorderRef.current = null;
            videoRecorderRef.current = null;
            audioRecorderRef.current = null;

            setRecordingInProgress(false);
            setRecordingPaused(false);

            const uploaded = [];
            for (let i = 0; i < stopTasks.length; i++) {
                const task = stopTasks[i];
                const result = stopped[i];
                if (result.status !== 'fulfilled') {
                    console.error('[LocalStudio] Failed to stop recorder:', task.kind, result.reason);
                    continue;
                }

                const file = result.value?.file;
                if (!file) continue;

                const assetId = await UploadAsset({
                    projectId: projectIdRef.current,
                    file: file,
                    assetTitle: task.assetTitle,
                    filetype: task.filetype,
                    isRecording: true,
                    isScreenRecording: task.isScreenRecording ?? false,
                });

                uploaded.push({ kind: task.kind, assetId, filetype: task.filetype });
            }

            const screenAsset = uploaded.find(u => u.kind === 'screen');
            const secondaryAsset = uploaded.find(u => u.kind === 'secondary');

            if (onRecordingComplete) {
                onRecordingComplete({
                    projectId: projectIdRef.current,
                    assetId: screenAsset?.assetId || secondaryAsset?.assetId,
                    filetype: screenAsset?.filetype || secondaryAsset?.filetype,
                    screenAssetId: screenAsset?.assetId,
                    secondaryAssetId: secondaryAsset?.assetId,
                });
            }

            await handleLeave();
        } catch (err) {
            console.error('[LocalStudio] Failed to stop recording:', err);
        } finally {
            setIsStopping(false);
        }
    };

    const handleLeave = async () => {
        if (recordingInProgress) {
            await handleStopRecording();
            return;
        }

        if (previewStreamRef.current) {
            previewStreamRef.current.getTracks().forEach(track => track.stop());
            previewStreamRef.current = null;
        }
        if (screenStreamRef.current) {
            screenStreamRef.current.getTracks().forEach(track => track.stop());
            screenStreamRef.current = null;
            setScreenStream(null);
        }

        if (onLeave) {
            onLeave();
        } else {
            navigate("/dashboard");
            setTimeout(() => {
                window.location.reload();
            }, 100);
        }
    };

    // Dropdown styles for upward opening - refined
    const selectMenuProps = {
        anchorOrigin: { vertical: 'top', horizontal: 'left' },
        transformOrigin: { vertical: 'bottom', horizontal: 'left' },
        PaperProps: {
            sx: {
                maxHeight: 320,
                minWidth: 220,
                borderRadius: '10px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                border: '1px solid var(--app-border-subtle)',
                mt: -0.5,
                '& .MuiMenuItem-root': {
                    minHeight: 40,
                    fontSize: '0.875rem',
                    px: 1.5,
                    color: 'var(--app-text-primary)',
                    '&:hover': {
                        backgroundColor: 'rgba(43,107,253,0.06)',
                    },
                    '&.Mui-selected': {
                        backgroundColor: 'rgba(43,107,253,0.08)',
                        '&:hover': {
                            backgroundColor: 'rgba(43,107,253,0.12)',
                        },
                    },
                },
            },
        },
    };


    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', minHeight: 0, backgroundColor: 'var(--app-bg-canvas)' }}>
            {/* Preview Area - takes remaining space */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Preview container with subtle framing */}
                <Box
                    sx={{
                        width: '100%',
                        height: isScreenLayoutMode ? 'auto' : '100%',
                        aspectRatio: isScreenLayoutMode ? getScreenAspectRatio() : undefined,
                        maxWidth: isScreenLayoutMode ? '1280px' : (isVideoMode ? '960px' : '800px'),
                        maxHeight: isScreenLayoutMode ? '720px' : (isVideoMode ? '540px' : '400px'),
                        backgroundColor: 'var(--app-bg-subtle)',
                        borderRadius: '12px',
                        border: '1px solid var(--app-border-subtle)',
                        overflow: 'hidden',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {!hasAnyInputSelected ? (
                        // No inputs selected
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            gap: 1,
                            color: 'var(--app-text-subtle)',
                        }}>
                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 400 }}>
                                Please select an input below
                            </Typography>
                        </Box>
                    ) : hasScreenStream ? (
                        // Screen preview with optional webcam overlay
                        <>
                            <video
                                ref={screenVideoPreviewRef}
                                autoPlay
                                playsInline
                                muted
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    backgroundColor: '#000',
                                }}
                            />
                            {isVideoMode && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        left: 16,
                                        bottom: 16,
                                        width: 180,
                                        height: 180,
                                        borderRadius: '50%',
                                        overflow: 'hidden',
                                        border: '1px solid var(--app-border-subtle)',
                                        boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
                                        backgroundColor: 'var(--app-bg-surface)',
                                    }}
                                >
                                    <video
                                        ref={videoPreviewRef}
                                        autoPlay
                                        playsInline
                                        muted
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            transform: 'scaleX(-1)',
                                        }}
                                    />
                                </Box>
                            )}
                        </>
                    ) : isVideoMode ? (
                        // Video preview with audio indicator
                        <>
                            <video
                                ref={videoPreviewRef}
                                autoPlay
                                playsInline
                                muted
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    transform: 'scaleX(-1)', // Mirror for selfie view
                                }}
                            />
                            {/* Vertical audio level indicator */}
                            {selectedAudioDevice && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        right: 16,
                                        bottom: 16,
                                        width: 4,
                                        height: '30%',
                                        backgroundColor: 'rgba(255,255,255,0.2)',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'flex-end',
                                    }}
                                >
                                    <Box
                                        ref={audioIndicatorRef}
                                        sx={{
                                            width: '100%',
                                            height: '10%',
                                            backgroundColor: 'rgba(255,255,255,0.9)',
                                            borderRadius: 2,
                                            transition: 'height 0.05s ease-out',
                                        }}
                                    />
                                </Box>
                            )}
                        </>
                    ) : (
                        // Audio visualizer canvas
                        <canvas
                            ref={canvasRef}
                            width={800}
                            height={400}
                            style={{
                                width: '100%',
                                height: '100%',
                            }}
                        />
                    )}
                </Box>

                {/* Countdown overlay - softer, calmer */}
                {countdown !== null && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: isDarkMode ? 'rgba(18,20,24,0.88)' : 'rgba(255,255,255,0.88)',
                            backdropFilter: 'blur(4px)',
                        }}
                    >
                        <Box
                            key={countdown} // Key for animation reset
                            sx={{
                                color: 'var(--app-text-primary)',
                                fontSize: '96px',
                                fontWeight: 600,
                                fontFamily: 'Inter, sans-serif',
                                lineHeight: 1,
                                animation: 'countdownPop 0.3s ease-out',
                                '@keyframes countdownPop': {
                                    '0%': { 
                                        opacity: 0,
                                        transform: 'scale(1.05)',
                                    },
                                    '100%': { 
                                        opacity: 1,
                                        transform: 'scale(1)',
                                    },
                                },
                            }}
                        >
                            {countdown}
                        </Box>
                        <Box
                            sx={{
                                color: 'var(--app-text-muted)',
                                fontSize: '12px',
                                fontWeight: 500,
                                letterSpacing: '0.05em',
                                textTransform: 'uppercase',
                                mt: 2,
                            }}
                        >
                            Recording starts in…
                        </Box>
                    </Box>
                )}

                {/* Recording indicator - refined, calmer */}
                {recordingInProgress && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 24,
                            right: 24,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            backgroundColor: 'rgba(0,0,0,0.4)',
                            px: 1.5,
                            py: 0.75,
                            borderRadius: '16px',
                        }}
                    >
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: '#FF4444',
                                animation: recordingPaused ? 'none' : 'slowPulse 2.2s ease-in-out infinite',
                                '@keyframes slowPulse': {
                                    '0%, 100%': { opacity: 1 },
                                    '50%': { opacity: 0.6 },
                                },
                            }}
                        />
                        <Box sx={{ color: 'white', fontSize: '0.8rem', fontWeight: 500 }}>
                            {recordingPaused ? 'Paused' : 'Recording'}
                        </Box>
                    </Box>
                )}

                {/* Stopping indicator - softer */}
                {isStopping && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: isDarkMode ? 'rgba(18,20,24,0.88)' : 'rgba(255,255,255,0.88)',
                            backdropFilter: 'blur(4px)',
                        }}
                    >
                        <Box sx={{ color: 'var(--app-text-primary)', fontSize: '1rem', fontWeight: 500 }}>
                            Finalizing recording...
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Control Bar - three zones: Exit | Inputs | Primary Action */}
            <Box
                sx={{
                    height: CONTROL_BAR_HEIGHT,
                    minHeight: CONTROL_BAR_HEIGHT,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    px: 2.5,
                    backgroundColor: 'var(--app-bg-surface)',
                    borderTop: '1px solid var(--app-border-subtle)',
                }}
            >
                {/* Exit zone */}
                <IconButton
                    onClick={handleLeave}
                    disabled={isStopping}
                    sx={{
                        color: 'var(--app-text-muted)',
                        '&:hover': { backgroundColor: 'var(--app-bg-subtle)' },
                    }}
                >
                    <CloseIcon sx={{ fontSize: 22 }} />
                </IconButton>

                {/* Device selectors - side by side */}
                {!recordingInProgress && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {/* Camera dropdown */}
                    <FormControl size="small">
                        <Select
                            value={selectedVideoDevice}
                            onChange={(e) => setSelectedVideoDevice(e.target.value)}
                            displayEmpty
                            disabled={recordingInProgress || isStopping}
                            IconComponent={KeyboardArrowDownIcon}
                            MenuProps={selectMenuProps}
                            sx={{
                                width: 160,
                                '& .MuiSelect-select': {
                                    py: 0.75,
                                    px: 1.25,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    fontSize: '0.825rem',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'var(--app-border-subtle)',
                                    borderRadius: '6px',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'var(--app-border-muted)',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'var(--app-border-muted)',
                                    borderWidth: 1,
                                },
                                backgroundColor: 'var(--app-bg-surface)',
                            }}
                            renderValue={(value) => {
                                const deviceIndex = value ? videoDevices.findIndex(d => d.deviceId === value) : -1;
                                const deviceLabel = value
                                    ? (videoDevices[deviceIndex]?.label || (deviceIndex >= 0 ? `Camera ${deviceIndex + 1}` : 'Camera'))
                                    : 'No camera';

                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, overflow: 'hidden' }}>
                                        <VideocamOutlined sx={{ fontSize: 18, color: value ? 'var(--app-icon-primary)' : 'var(--app-text-subtle)', flexShrink: 0 }} />
                                        <span style={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            color: value ? 'var(--app-text-primary)' : 'var(--app-text-muted)',
                                        }}>
                                            {deviceLabel}
                                        </span>
                                    </Box>
                                );
                            }}
                        >
                            {/* Device list */}
                            {videoDevices.map(device => (
                                <MenuItem 
                                    key={device.deviceId} 
                                    value={device.deviceId}
                                    sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between',
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    <span>{device.label || `Camera ${videoDevices.indexOf(device) + 1}`}</span>
                                    {selectedVideoDevice === device.deviceId && (
                                        <CheckIcon sx={{ fontSize: 16, color: 'var(--app-icon-primary)', ml: 1 }} />
                                    )}
                                </MenuItem>
                            ))}
                            {/* Divider before "No camera" */}
                            <Divider sx={{ my: 0.5 }} />
                            <MenuItem 
                                value=""
                                sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between',
                                    fontSize: '0.875rem',
                                    color: 'var(--app-text-muted)',
                                }}
                            >
                                <span>No camera</span>
                                {selectedVideoDevice === '' && (
                                    <CheckIcon sx={{ fontSize: 16, color: 'var(--app-icon-primary)', ml: 1 }} />
                                )}
                            </MenuItem>
                        </Select>
                    </FormControl>

                    {/* Mic dropdown */}
                    <FormControl size="small">
                        <Select
                            value={selectedAudioDevice}
                            onChange={(e) => setSelectedAudioDevice(e.target.value)}
                            displayEmpty
                            disabled={recordingInProgress || isStopping}
                            IconComponent={KeyboardArrowDownIcon}
                            MenuProps={selectMenuProps}
                            sx={{
                                width: 160,
                                '& .MuiSelect-select': {
                                    py: 0.75,
                                    px: 1.25,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    fontSize: '0.825rem',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'var(--app-border-subtle)',
                                    borderRadius: '6px',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'var(--app-border-muted)',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'var(--app-border-muted)',
                                    borderWidth: 1,
                                },
                                backgroundColor: 'var(--app-bg-surface)',
                            }}
                            renderValue={(value) => {
                                const deviceIndex = value ? audioDevices.findIndex(d => d.deviceId === value) : -1;
                                const deviceLabel = value
                                    ? (audioDevices[deviceIndex]?.label || (deviceIndex >= 0 ? `Microphone ${deviceIndex + 1}` : 'Microphone'))
                                    : 'No microphone';

                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, overflow: 'hidden' }}>
                                        <MicNoneOutlined sx={{ fontSize: 18, color: value ? 'var(--app-icon-primary)' : 'var(--app-text-subtle)', flexShrink: 0 }} />
                                        <span style={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            color: value ? 'var(--app-text-primary)' : 'var(--app-text-muted)',
                                        }}>
                                            {deviceLabel}
                                        </span>
                                    </Box>
                                );
                            }}
                        >
                            {/* Device list */}
                            {audioDevices.map(device => (
                                <MenuItem 
                                    key={device.deviceId} 
                                    value={device.deviceId}
                                    sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between',
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    <span>{device.label || `Microphone ${audioDevices.indexOf(device) + 1}`}</span>
                                    {selectedAudioDevice === device.deviceId && (
                                        <CheckIcon sx={{ fontSize: 16, color: 'var(--app-icon-primary)', ml: 1 }} />
                                    )}
                                </MenuItem>
                            ))}
                            {/* Divider before "No microphone" */}
                            <Divider sx={{ my: 0.5 }} />
                            <MenuItem 
                                value=""
                                sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between',
                                    fontSize: '0.875rem',
                                    color: 'var(--app-text-muted)',
                                }}
                            >
                                <span>No microphone</span>
                                {selectedAudioDevice === '' && (
                                    <CheckIcon sx={{ fontSize: 16, color: 'var(--app-icon-primary)', ml: 1 }} />
                                )}
                            </MenuItem>
                        </Select>
                    </FormControl>

                    {/* Screen share control */}
                    <Box
                        ref={screenShareButtonRef}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleToggleScreenShare();
                        }}
                        sx={{
                            width: 160,
                            border: '1px solid var(--app-border-subtle)',
                            borderRadius: '6px',
                            backgroundColor: 'var(--app-bg-surface)',
                            px: 1.25,
                            py: 0.75,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            minWidth: 0,
                            cursor: (recordingInProgress || countdown !== null || isStopping) ? 'not-allowed' : 'pointer',
                            opacity: (recordingInProgress || countdown !== null || isStopping) ? 0.6 : 1,
                            pointerEvents: (recordingInProgress || countdown !== null || isStopping) ? 'none' : 'auto',
                            '&:hover': {
                                backgroundColor: 'var(--app-bg-subtle)',
                            },
                        }}
                    >
                        <ScreenShareOutlined sx={{ fontSize: 18, color: 'var(--app-icon-primary)', flexShrink: 0 }} />
                        <span style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: 'var(--app-text-primary)',
                            fontSize: '0.825rem',
                        }}>
                            {getScreenShareLabel()}
                        </span>
                    </Box>
                </Box>
                )}

                {/* Pause/Resume button (only while recording) */}
                {recordingInProgress && (
                    <Tooltip title={recordingPaused ? 'Resume recording' : 'Pause recording'}>
                        <span>
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    void handleTogglePauseRecording();
                                }}
                                disabled={isStopping}
                                aria-label={recordingPaused ? 'Resume recording' : 'Pause recording'}
                                sx={{
                                    color: 'var(--app-text-muted)',
                                    '&:hover': { backgroundColor: 'var(--app-bg-subtle)' },
                                }}
                            >
                                {recordingPaused ? (
                                    <PlayArrowOutlined sx={{ fontSize: 22 }} />
                                ) : (
                                    <PauseOutlined sx={{ fontSize: 22 }} />
                                )}
                            </IconButton>
                        </span>
                    </Tooltip>
                )}

                {/* Primary Action - Record/Stop button */}
                <Box
                    onClick={(e) => {
                        e.stopPropagation();
                        if (recordingInProgress) {
                            handleStopRecording();
                        } else {
                            handleStartRecording();
                        }
                    }}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 0.75,
                        backgroundColor: '#E53935',
                        borderRadius: '6px',
                        cursor: (isStopping || !canStartRecording) ? 'not-allowed' : 'pointer',
                        opacity: (isStopping || !canStartRecording) ? 0.6 : 1,
                        pointerEvents: (isStopping || !canStartRecording) ? 'none' : 'auto',
                        transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        minWidth: recordingInProgress ? 90 : undefined,
                        '&:hover': {
                            backgroundColor: isStopping ? undefined : '#C62828',
                            boxShadow: isStopping ? undefined : '0 2px 6px rgba(0,0,0,0.15)',
                        },
                        '&:active': {
                            backgroundColor: isStopping ? undefined : '#B71C1C',
                        },
                    }}
                >
                    {recordingInProgress ? (
                        <>
                            {/* Flashing stop square */}
                            <Box
                                sx={{
                                    width: 12,
                                    height: 12,
                                    backgroundColor: 'white',
                                    borderRadius: 0.5,
                                    animation: recordingPaused ? 'none' : 'stopFlash 1s ease-in-out infinite',
                                    '@keyframes stopFlash': {
                                        '0%, 100%': { opacity: 1 },
                                        '50%': { opacity: 0.4 },
                                    },
                                    pointerEvents: 'none',
                                }}
                            />
                            <Box sx={{ 
                                color: 'white', 
                                fontSize: '0.825rem', 
                                fontWeight: 500,
                                fontVariantNumeric: 'tabular-nums',
                                pointerEvents: 'none',
                            }}>
                                {formatRecordTime()}
                            </Box>
                        </>
                    ) : (
                        <>
                            <FiberManualRecordIcon sx={{ color: 'white', fontSize: 18 }} />
                            <Box sx={{ color: 'white', fontSize: '0.825rem', fontWeight: 500 }}>Record</Box>
                        </>
                    )}
                </Box>

                {/* Teleprompter button */}
                <Tooltip title="Teleprompter">
                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation();
                            setTeleprompterOpen((prev) => !prev);
                        }}
                        sx={{
                            color: teleprompterOpen ? 'var(--app-text-primary)' : 'var(--app-text-muted)',
                            backgroundColor: teleprompterOpen ? 'var(--app-bg-subtle)' : 'transparent',
                            '&:hover': { backgroundColor: 'var(--app-bg-subtle)' },
                            borderRadius: '6px',
                        }}
                    >
                        <SubtitlesOutlined sx={{ fontSize: 20 }} />
                    </IconButton>
                </Tooltip>

                {/* Settings button */}
                <Tooltip title="Recorder settings">
                    <IconButton
                        onClick={(e) => setSettingsAnchorEl(e.currentTarget)}
                        disabled={recordingInProgress || isStopping}
                        sx={{
                            color: 'var(--app-text-muted)',
                            '&:hover': { backgroundColor: 'var(--app-bg-subtle)' },
                            borderRadius: '6px',
                        }}
                    >
                        <SettingsIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Tooltip>

                {/* Settings Popover */}
                <Popover
                    open={settingsOpen}
                    anchorEl={settingsAnchorEl}
                    onClose={() => setSettingsAnchorEl(null)}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    PaperProps={{
                        sx: {
                            borderRadius: '12px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                            minWidth: 280,
                            p: 2,
                            border: '1px solid var(--app-border-subtle)',
                            backgroundColor: 'var(--app-bg-surface)',
                            color: 'var(--app-text-primary)',
                        },
                    }}
                >
                    {/* Microphone audio section */}
                    <Typography 
                        variant="subtitle2" 
                        sx={{ 
                            fontWeight: 600, 
                            color: 'var(--app-text-primary)',
                            mb: 1.5,
                        }}
                    >
                        Microphone audio
                    </Typography>

                    {/* Studio Sound toggle */}
                    <Tooltip 
                        title="Reduce background noise in your recording"
                        placement="left"
                    >
                        <Box 
                            sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                mb: 1,
                            }}
                        >
                            <Typography variant="body2" sx={{ color: 'var(--app-text-muted)' }}>
                                Noise suppression
                            </Typography>
                            <Switch
                                checked={noiseSuppressionEnabled}
                                onChange={(e) => setNoiseSuppressionEnabled(e.target.checked)}
                                size="small"
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: '#2B6BFD',
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: '#2B6BFD',
                                    },
                                    '& .MuiSwitch-track': {
                                        backgroundColor: 'var(--app-border-subtle)',
                                    },
                                }}
                            />
                        </Box>
                    </Tooltip>

                    {/* Auto gain control toggle */}
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            mb: 2,
                        }}
                    >
                        <Typography variant="body2" sx={{ color: 'var(--app-text-muted)' }}>
                            Auto gain control
                        </Typography>
                        <Switch
                            checked={autoGainControlEnabled}
                            onChange={(e) => setAutoGainControlEnabled(e.target.checked)}
                            size="small"
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: '#2B6BFD',
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: '#2B6BFD',
                                },
                                '& .MuiSwitch-track': {
                                    backgroundColor: 'var(--app-border-subtle)',
                                },
                            }}
                        />
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    {/* Camera section */}
                    <Typography 
                        variant="subtitle2" 
                        sx={{ 
                            fontWeight: 600, 
                            color: 'var(--app-text-primary)',
                            mb: 1.5,
                        }}
                    >
                        Camera
                    </Typography>

                    {/* Default resolution */}
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="body2" sx={{ color: 'var(--app-text-muted)' }}>
                            Default resolution
                        </Typography>
                        <FormControl size="small">
                            <Select
                                value={selectedResolution}
                                onChange={(e) => setSelectedResolution(e.target.value)}
                                IconComponent={KeyboardArrowDownIcon}
                                renderValue={(value) => value}
                                MenuProps={{
                                    anchorOrigin: { vertical: 'top', horizontal: 'right' },
                                    transformOrigin: { vertical: 'bottom', horizontal: 'right' },
                                    PaperProps: {
                                        sx: {
                                            minWidth: 120,
                                            borderRadius: '10px',
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                                            border: '1px solid var(--app-border-subtle)',
                                            backgroundColor: 'var(--app-bg-surface)',
                                            mt: -0.5,
                                            '& .MuiMenuItem-root': {
                                                minHeight: 36,
                                                fontSize: '0.875rem',
                                                px: 1.5,
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                color: 'var(--app-text-primary)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(43,107,253,0.06)',
                                                },
                                                '&.Mui-selected': {
                                                    backgroundColor: 'transparent',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(43,107,253,0.06)',
                                                    },
                                                },
                                            },
                                        },
                                    },
                                }}
                                sx={{
                                    minWidth: 90,
                                    '& .MuiSelect-select': {
                                        py: 0.75,
                                        px: 1.25,
                                        fontSize: '0.875rem',
                                        color: 'var(--app-text-primary)',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'var(--app-border-subtle)',
                                        borderRadius: '8px',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'var(--app-border-muted)',
                                    },
                                    backgroundColor: 'var(--app-bg-surface)',
                                }}
                            >
                                {RESOLUTION_OPTIONS.map(option => (
                                    <MenuItem key={option.label} value={option.label}>
                                        <span>{option.label}</span>
                                        {selectedResolution === option.label && (
                                            <CheckIcon sx={{ fontSize: 16, color: 'var(--app-icon-primary)', ml: 2 }} />
                                        )}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Popover>
            </Box>
            <TeleprompterWindow
                open={teleprompterOpen}
                onClose={() => setTeleprompterOpen(false)}
                recordingInProgress={recordingInProgress}
            />
        </Box>
    );
};

export default LocalStudio;
